/**
 * 解决方案页数据（功能域落地页）
 *
 * ⚠️ 写作纪律：本文件每条能力/实现描述都必须能回溯到真实代码或已核实事实。
 *    事实来源：~/HermesWork/hermes/hoyotech-promo/09-产品能力事实基线.md
 *    禁止写：产量预测、精准灌溉（口径应为「水肥一体/智能灌溉」）、自研大模型、
 *            任何未经实测的准确率/降本百分比。
 *
 * 归属业务线用 line 字段指定（agriculture / mes / iot / enterprise），
 * 页面主题色与面包屑从 site.js 的 businessLines 里取，保证与全站一致。
 */
// 批次二数据分文件维护（减少单文件体积，便于分批追加）：
//   solutions-iot.js      工业物联网 · 3 页
//   solutions-farm-ops.js 农业运营与合规 · 6 页
import { iotSolutions } from './solutions-iot'
import { farmOpsSolutions } from './solutions-farm-ops'

export const solutions = [
  /* ============================ 农业 · 接入 ============================ */
  {
    slug: 'device-access',
    line: 'agriculture',
    name: '农业设备接入管理',
    en: 'Device Access',
    eyebrow: 'Device Access',
    tagline: '把分散的传感器和网关，变成系统里可调用的对象',
    summary:
      '网关发现与诊断、协议解析、物模型模板、采集策略四件事合在一起，才叫「设备接入」。' +
      '它是所有监测与控制的前提——这一步做不好，后面的看板和联动都是空中楼阁。',
    overview:
      '农业现场的设备很少来自同一家厂商：温湿度传感器是一个牌子，水肥一体机是另一个牌子，' +
      '摄像头又是第三个。如果平台不能把这些设备统一纳管，结果就是告警散落在不同 App 里、' +
      '控制要来回切换、运维成本随设备数量一起上升。设备接入管理要解决的，正是「把物理设备变成' +
      '系统里可被调用的对象」这一步。',
    pains: [
      { title: '品牌一多，各配一套 App', desc: '同园区三四种品牌的设备各有一套管理端，数据不通，出了问题要挨个查。' },
      { title: '点表和实际对不上', desc: '设备装了、数据也上来了，但读数与实际不符，排查时不知道是接线、寄存器还是映射的问题。' },
      { title: '新增设备要改代码', desc: '每接一个新型号都要开发介入，周期长、成本高，现场等不起。' }
    ],
    capabilities: [
      { icon: 'wifi', title: '网关发现与诊断', desc: '把边缘网关上电后纳入平台管理，提供诊断入口，快速判断链路是否通、设备是否在线。', tags: ['网关纳管', '在线诊断', '链路自检'] },
      { icon: 'link', title: '协议解析', desc: '协议驱动可插拔，新增设备型号主要是补协议与点表，不必改动平台主干。', tags: ['协议可插拔', 'Modbus-RTU', 'MQTT'] },
      { icon: 'layers', title: '物模型模板', desc: '把设备能力抽象成物模型，同一模板可复用到同型号设备，避免逐台重复配置。', tags: ['物模型', '模板复用', '属性/事件'] },
      { icon: 'database', title: '采集策略', desc: '按点位配置采集频率与上报方式，兼顾数据密度与上行流量。', tags: ['采集频率', '上报策略', '离线缓存'] },
      { icon: 'gear', title: '设备台账与分组', desc: '设备档案、分组与归属管理，支撑按区域、按类型批量查看与运维。', tags: ['设备台账', '分组管理', '批量运维'] },
      { icon: 'cpu', title: 'DI / DO 通道管理', desc: '数字量输入输出通道与继电器绑定关系可查，现场按钮与远程控制行为一致。', tags: ['DI/DO', '通道绑定', '状态回读'] }
    ],
    how: [
      { title: '现场勘察与点位设计', desc: '先确认要监测哪些区域、装哪些传感器、控哪些设备，形成点位清单。' },
      { title: '网关纳管与联调', desc: '网关接入平台并完成发现诊断，确认上行链路稳定。' },
      { title: '协议与点表核对', desc: '逐点位核对寄存器地址、量程与单位，确保系统里的读数与现场仪表一致。' },
      { title: '物模型建模', desc: '按设备类型建立物模型并套用模板，同一型号设备批量生效。' },
      { title: '采集策略与验证', desc: '配置采集频率后连续观察一段时间，确认无断点、无异常跳变再交付。' }
    ],
    specs: [
      { k: '接入方式', v: '边缘网关承担现场采集与上行，支持 Modbus-RTU 采集与 MQTT 上下行' },
      { k: '协议扩展', v: '协议驱动以独立模块存在，新增协议不影响业务主干' },
      { k: '物模型', v: '按设备类型建立模型模板，同型号设备可复用' },
      { k: '数据解析', v: '原始报文到业务量的解析过程独立可查，便于定位取值偏差' },
      { k: '上行链路', v: '支持项目现场可用的组网方式；具体覆盖需现场勘查确认' }
    ],
    faq: [
      { q: '我们已经买了别的品牌的传感器，能接进来吗？', a: '关键看平台有没有完整的协议解析与物模型能力。具备这两项时，接入新品牌主要是补协议与点表的工作量，不需要改平台主干。具体型号是否已适配，需要现场勘查确认。' },
      { q: '老式大棚没有网络怎么办？', a: '采集与控制在设备侧由边缘网关完成，网关再向上行通信，所以关键是大棚有没有可用的上行链路。用有线还是 4G、覆盖是否满足，要看现场条件。' },
      { q: '接进来的数据不准，怎么排查？', a: '按四层依次确认：接线与供电是否正确、寄存器地址与量程是否与设备手册一致、系统里的点表映射是否对得上、传感器是否需要标定。平台侧原始报文与解析结果可查，能把问题定位到具体一层。' },
      { q: '先接多少设备比较合适？', a: '建议先从一个大棚或一条产线开始：接一种传感器加一个可控设备，把「采集 → 告警 → 下发 → 执行留痕」跑通一遍，再复制到其他区域。这样投入可控，也能提前暴露协议适配与点位布置的问题。' }
    ],
    related: ['device-control', 'farm-machine-asset']
  },

  /* ============================ 农业 · 管控 ============================ */
  {
    slug: 'device-control',
    line: 'agriculture',
    name: '农业设备管控',
    en: 'Device Control',
    eyebrow: 'Device Control',
    tagline: '让指令只有一个出口，避免多入口抢设备',
    summary:
      '设备总览、远程控制、定时任务、自动规则引擎、故障巡检维保、视频安防——' +
      '这些合起来是「用池子」。它最重要的设计不是功能多，而是指令下发必须唯一收口。',
    overview:
      '园区里能改变设备状态的东西往往有三拨人：现场人员手动操作、预设的定时任务、' +
      '以及根据环境数据触发的自动联动。如果这三条路径各自向设备下发指令，' +
      '同一台风机或阀门就可能被反复拉扯。合理的设计是让环境阈值只产生「告警与控制意图」，' +
      '真正的指令一律由自动规则引擎统一收口下发。',
    pains: [
      { title: '多个入口抢同一台设备', desc: '人工、定时、告警联动同时下发指令，设备状态反复跳变，责任也说不清。' },
      { title: '操作没有留痕', desc: '谁在什么时候开了哪台设备、是手动还是自动，事后查不到。' },
      { title: '故障靠人发现', desc: '设备离线、异常停机要等到需要用它时才发现，耽误生产。' }
    ],
    capabilities: [
      { icon: 'activity', title: '设备总览', desc: '在线状态、关键指标与告警集中呈现，一屏掌握全场设备态势。', tags: ['在线状态', '集中总览', '异常聚合'] },
      { icon: 'zap', title: '远程控制与指令下发', desc: '远程开关设备、下发指令并回读执行结果，形成操作记录。', tags: ['远程控制', '指令下发', '结果回读'] },
      { icon: 'clock', title: '定时任务', desc: '按时间计划执行设备动作，适合规律性强、条件稳定的场景。', tags: ['时间计划', '周期执行', '执行记录'] },
      { icon: 'gear', title: '自动规则引擎', desc: '按环境与墒情条件自动触发联动，是全部指令的统一收口。', tags: ['条件联动', '指令收口', '执行留痕'] },
      { icon: 'shield', title: '故障、巡检与维保', desc: '设备故障记录、巡检任务与维保计划工单闭环，减少非计划停机。', tags: ['故障记录', '巡检计划', '维保工单'] },
      { icon: 'play', title: '视频安防与电子围栏', desc: '现场视频接入、录像与电子围栏告警，与设备告警统一查看。', tags: ['视频接入', '电子围栏', '告警联动'] }
    ],
    how: [
      { title: '先跑通单点控制', desc: '选一台可控设备完成远程开关与状态回读，验证链路可用。' },
      { title: '配置定时任务', desc: '把规律性动作交给时间计划执行，减少人工到现场的操作。' },
      { title: '建立自动规则', desc: '把环境条件与设备动作写成规则，让系统按现场状况自动响应。' },
      { title: '接入告警与工单', desc: '故障与告警转入工单流程，明确处理人、处理时限与结果。' },
      { title: '纳入巡检与维保', desc: '按周期生成巡检与维保任务，把设备管理从被动抢修变成计划管理。' }
    ],
    specs: [
      { k: '指令收口', v: '环境阈值只产生告警与控制意图，实际下发由自动规则引擎统一执行' },
      { k: '执行留痕', v: '设备动作与下发结果落账，可按设备与时间回溯' },
      { k: '控制方式', v: '支持远程手动、定时任务、规则联动三种方式并用，人工干预同样留痕' },
      { k: '能耗与运维', v: '设备能耗、故障、巡检与维保纳入统一视图' },
      { k: '现场一致性', v: '现场物理按钮与平台按钮行为一致，切换结果双向同步' }
    ],
    faq: [
      { q: '为什么强调「指令只有一个出口」？', a: '因为设备状态是共享资源。人工、定时、告警三条路径都能改设备时，如果各自下发，冲突不可避免——设备会被反复拉扯，出问题也无法归因。统一收口后，谁在什么时候因什么条件改了什么设备，都有据可查。' },
      { q: '自动联动会不会误操作？', a: '规则本身由现场配置并经过验证，且每次触发都留痕。建议先以「告警 + 人工确认」的方式跑一段时间，确认规则阈值合理后，再逐步放开为自动下发。' },
      { q: '设备离线了能知道吗？', a: '可以。设备在线状态与故障记录在总览中集中呈现，配合巡检与维保计划，能把被动抢修变成计划性维护。' },
      { q: '视频和设备的告警是分开看的吗？', a: '不需要分开。视频安防、电子围栏与设备告警在同一套视图里，便于判断「什么时候、哪个位置、发生了什么」。' }
    ],
    related: ['farm-video-security', 'device-access']
  },

  /* ======================== 农业 · 环境监测 ======================== */
  {
    slug: 'environment-monitoring',
    line: 'agriculture',
    name: '农业环境监测',
    en: 'Environment Monitoring',
    eyebrow: 'Monitoring',
    tagline: '数据要先可信，再谈用来做决策',
    summary:
      '实时监测、历史曲线、数据质量评估、阈值告警、气象墒情联动。' +
      '其中数据质量评估决定数据能不能直接拿来触发控制——没有这一步，脏数据会变成错误决策。',
    overview:
      '环境监测最常见的失败方式不是「没有数据」，而是「数据不可信」：传感器跳变、断点、' +
      '离群值不断触发告警，管理员很快对告警脱敏，最后干脆不看。所以一套可用的监测系统，' +
      '除了把数据采上来，还要回答三个问题：这条数据可信吗、这个告警该不该发、这条曲线说明了什么。',
    pains: [
      { title: '告警太吵，最后没人看', desc: '脏数据频繁触发阈值告警，管理员逐步脱敏，真正的异常被淹没。' },
      { title: '只有实时值，没有趋势', desc: '看不到历史曲线与变化趋势，判断只能凭当下一个数字。' },
      { title: '气象与田间数据脱节', desc: '外部气象条件与棚内环境、墒情数据分属不同系统，无法联动判断。' }
    ],
    capabilities: [
      { icon: 'thermometer', title: '实时监测', desc: '空气温湿度、光照、CO₂、土壤温湿度与墒情等多参数实时呈现。', tags: ['多参数', '实时刷新', '分区展示'] },
      { icon: 'chart', title: '历史曲线与趋势', desc: '按时段回看数据变化，支撑复盘与种植模型沉淀。', tags: ['历史曲线', '多参数对比', '时段回看'] },
      { icon: 'activity', title: '数据质量评估', desc: '识别跳变、断点与离群值，避免脏数据直接触发告警与灌溉决策。', tags: ['跳变识别', '断点检测', '离群值'] },
      { icon: 'bell', title: '阈值告警', desc: '按作物与生育期配置阈值，超限产生告警与控制意图。', tags: ['分级阈值', '告警确认', '处理闭环'] },
      { icon: 'sun', title: '气象墒情联动', desc: '接入外部气象数据源，为灌溉与通风判断提供依据。', tags: ['多气象源', '墒情联动', '灾害预警'] },
      { icon: 'shield', title: '环境质量与灾害预警', desc: '环境质量评估与气象灾害预警规则，提前识别风险。', tags: ['环境质量', '灾害规则', '提前预警'] }
    ],
    how: [
      { title: '确定监测指标与点位', desc: '按作物与生产目标确定要监测的参数、数量与安装位置。' },
      { title: '接入并核对读数', desc: '设备接入后逐点位核对量程与单位，确保与现场仪表一致。' },
      { title: '观察数据质量', desc: '连续观察一段时间，识别跳变、断点与离群值，先解决数据源问题。' },
      { title: '配置分级阈值', desc: '按参数与时段设置告警阈值，避免过密告警导致脱敏。' },
      { title: '联动控制与复盘', desc: '把有效的阈值条件接入规则引擎，并用历史数据反过来优化阈值。' }
    ],
    specs: [
      { k: '告警口径', v: '阈值只产生告警与控制意图，不直接下发设备指令' },
      { k: '规则去重', v: '同一来源的阈值规则按唯一键约束，避免重复在役规则互相干扰' },
      { k: '灾害匹配', v: '气象灾害规则按「精确 → 同设备 → 语义」三级回退匹配' },
      { k: '时序存储', v: '高频采集数据进入时序库存储，趋势查询经聚合缓存优化' },
      { k: '多源气象', v: '气象数据源可配置，便于更换或并用' }
    ],
    faq: [
      { q: '传感器数据经常跳变，怎么办？', a: '先区分是数据源问题还是平台问题。平台侧有数据质量评估能力，能识别跳变、断点与离群值；如果长期不准，通常是安装位置、供电或标定问题，需要现场排查。' },
      { q: '告警太多，怎么让它「准而不吵」？', a: '三点：一是先做数据质量过滤，避免脏数据触发；二是阈值按参数和时段分级设置，而不是一刀切；三是告警要有确认与处理记录，跑一段时间后根据实际误报情况回调阈值。' },
      { q: '为什么阈值不直接控制设备？', a: '阈值判定容易受瞬时波动影响，直接下发会导致设备频繁动作。更稳妥的做法是阈值只产生告警与控制意图，由规则引擎按更完整的条件决定是否下发。' },
      { q: '气象数据是必须的吗？', a: '不是必须，但很有价值。外部气象与棚内环境、墒情数据联动后，灌溉与通风的判断会更贴近实际，也能提前识别灾害性天气。' }
    ],
    related: ['water-fertilizer', 'farm-weather']
  },

  /* ======================= 农业 · 水肥一体 ======================= */
  {
    slug: 'water-fertilizer',
    line: 'agriculture',
    name: '水肥一体化与智能灌溉',
    en: 'Water & Fertilizer',
    eyebrow: 'Irrigation',
    tagline: '把老师傅的手感，变成可复用的配方与计划',
    summary:
      '配方管理、灌溉计划、执行记录、用水量统计，加上设施场景的 EC/pH 闭环与多通道施肥。' +
      '核心不是「装一台一体机」，而是把灌溉这件事纳入可计划、可追溯的管理。',
    overview:
      '水肥灌溉一直是最依赖经验的环节：浇多久、施多少，往往凭手感和当天心情。' +
      '问题不在于经验本身，而在于经验无法沉淀和复制——同一位师傅的做法很难完整交给下一个人。' +
      '把配方与计划固化进系统之后，灌溉才从「个人手艺」变成「可执行、可复盘的标准动作」。',
    pains: [
      { title: '浇水施肥全凭手感', desc: '浇多久、施多少没有量化依据，不同人操作结果差异很大。' },
      { title: '灌溉记录事后补', desc: '执行情况靠回忆补记，查不到当时的墒情与用量，无法复盘优化。' },
      { title: '多个入口都能开阀', desc: '人工、定时、环境联动都可能开阀，容易重复灌溉或互相冲突。' }
    ],
    capabilities: [
      { icon: 'clipboard', title: '配方管理', desc: '把不同作物、不同生育期的施肥方案固化成配方，避免临时拍脑袋。', tags: ['生育期配方', '配方复用', '用量可控'] },
      { icon: 'clock', title: '灌溉计划', desc: '按作物、地块与时段安排灌溉，把计划交给系统执行。', tags: ['按地块', '按时段', '计划执行'] },
      { icon: 'file', title: '执行记录与用水量统计', desc: '每次灌溉的开关时间、时长与用量留痕，可按地块与时段统计。', tags: ['执行留痕', '用水统计', '按地块汇总'] },
      { icon: 'target', title: 'EC / pH 闭环控制', desc: '设施场景下按设定的 EC 与 pH 目标调节肥液，保证配比稳定。', tags: ['EC 闭环', 'pH 调节', '配比稳定'] },
      { icon: 'drop', title: '多通道施肥', desc: '支持多路肥液通道分别控制，满足不同养分配比需求。', tags: ['多通道', '分路控制', '按需配比'] },
      { icon: 'zap', title: '与规则引擎联动', desc: '把墒情与环境条件接入规则引擎，由统一出口决定何时灌溉。', tags: ['墒情联动', '统一下发', '避免抢阀'] }
    ],
    how: [
      { title: '盘点水源与管路', desc: '确认水源、泵阀、施肥机与管路分区，明确可控范围。' },
      { title: '固化配方', desc: '把现有的施肥经验整理成按作物与生育期的配方。' },
      { title: '编排灌溉计划', desc: '按地块与时段排计划，先跑通定时灌溉。' },
      { title: '接入墒情联动', desc: '把土壤墒情与环境条件接入规则引擎，实现按需灌溉。' },
      { title: '统计与优化', desc: '用执行记录与用水量数据复盘，反过来调整配方与阈值。' }
    ],
    specs: [
      { k: '配方与计划', v: '施肥配方与灌溉计划独立管理，计划可按作物、地块与时段编排' },
      { k: '执行留痕', v: '每次灌溉的开关时间与时长形成记录，可与农事记录关联' },
      { k: '设施闭环', v: '设施场景支持 EC / pH 闭环控制与多通道施肥' },
      { k: '用水统计', v: '按地块与时段统计用水量，为成本与节水管理提供依据' },
      { k: '控制出口', v: '与水肥相关的下发同样由统一出口执行，避免与人工操作抢阀' }
    ],
    faq: [
      { q: '我已经买了水肥一体机，能接进系统吗？', a: '前提是平台能通过设备接入把它纳管（协议解析与物模型）。能否直连、是否需要加网关或更换控制器，取决于该一体机的通信接口，需要现场确认。' },
      { q: '配方一定要很精确吗？', a: '不必一步到位。先从现有的成熟做法整理出基本配方，跑通计划灌溉，再用执行记录与产量数据逐步调整。配方是持续优化的结果，不是一开始就要算准的参数。' },
      { q: '怎么避免重复灌溉？', a: '关键是让所有灌溉入口统一：人工操作、定时计划与墒情联动都走同一个下发出口，并且每次执行都有记录可查，从机制上避免同一区域被重复浇灌。' },
      { q: '没有墒情传感器能做智能灌溉吗？', a: '可以做计划灌溉——按作物生育期与时段安排，比人工开关更稳定。要做按需灌溉则需要墒情数据支撑，建议在关键分区先布设少量测点。' }
    ],
    related: ['pest-ai', 'environment-monitoring']
  },

  /* ========================= 农业 · 虫情 AI ========================= */
  {
    slug: 'pest-ai',
    line: 'agriculture',
    name: '虫情 AI 识别与防治闭环',
    en: 'Pest AI',
    eyebrow: 'Pest AI',
    tagline: '识别不出来时敢说不知道，比认得多更重要',
    summary:
      'AI 识别给初判、农技人员复核确认、系统统计准确率，再由危害分级触发防治闭环。' +
      '关键在于：任何一个环节不成立，识别结果就是空的——绝不把「未配置」渲染成「识别成功」。',
    overview:
      '农业 AI 最容易被忽视的问题不是「识别得准不准」，而是「识别不出来时系统怎么表现」。' +
      '一个错误判断会直接导致用错药，代价不可逆。因此这套实现采取 fail-closed 设计：' +
      '缺图、服务未配置、提供方不支持、调用失败或响应无法解析，识别结论一律为空，' +
      '宁可明确报失败，也不给一个可能错误的答案。',
    pains: [
      { title: 'AI 说是这种虫，万一是别的', desc: '只给结论不给可信度，用错药的风险直接落到农药残留与防治效果上。' },
      { title: '识别完就没下文了', desc: '认出来是什么虫，但没有分级、没有处置、没有跟踪，等于认了个寂寞。' },
      { title: '模型在自家地里准不准，说不清', desc: '没有对照与统计，无法判断模型在本地作物与虫相上的真实表现。' }
    ],
    capabilities: [
      { icon: 'leaf', title: 'AI 虫情识别', desc: '上传现场照片后给出虫种初判与置信度，作为人工复核的起点。', tags: ['图像识别', '置信度', '初判参考'] },
      { icon: 'users', title: '人工复核', desc: '由农技人员确认或修正，AI 原值保留不覆盖，形成可对照的两份数据。', tags: ['复核流程', '原值保留', '可追溯'] },
      { icon: 'chart', title: '准确率统计', desc: '把 AI 初判与人工复核结果对照，长期看在本地作物上的实际表现。', tags: ['对账统计', '样本量', '趋势观察'] },
      { icon: 'shield', title: '危害分级与防治闭环', desc: '区分轻、中、重三级危害，中重级别强制关联处置并跟踪结果。', tags: ['三级分级', '强制处置', '结果追踪'] },
      { icon: 'key', title: '识别服务可配置', desc: 'AI 提供方通过配置管理、适配器可插拔，换服务不改业务系统。', tags: ['提供方可配', '适配器', '可插拔'] },
      { icon: 'file', title: '调用留痕', desc: '每次识别调用无论成功失败都记录明细，便于排查与统计。', tags: ['成功失败留痕', '错误区分', '可复盘'] }
    ],
    how: [
      { title: '现场拍照与上传', desc: '拍摄虫体照片并上传到虫情记录，照片是识别的必要条件。' },
      { title: 'AI 初判', desc: '调用配置好的识别服务，返回虫种与置信度；调用不成立则明确报失败。' },
      { title: '人工复核', desc: '农技人员确认或修正虫种，复核结论与 AI 原值分别留存。' },
      { title: '危害分级', desc: '判定危害程度为轻、中、重，中重级别触发防治要求。' },
      { title: '处置与跟踪', desc: '关联处置措施并跟踪结果形成闭环，处置记录进入农事档案。' }
    ],
    specs: [
      { k: '失败处理', v: '缺图片、服务未配置或停用、提供方无实现、调用失败、响应不可解析，均返回明确错误且识别结论为空' },
      { k: '「停用」口径', v: '配置被停用等同未配置，不回落旧结果、不假装成功' },
      { k: '调用超时', v: '识别调用默认 5 秒，可在 0.5 秒到 60 秒之间配置' },
      { k: '危害等级', v: '轻 / 中 / 重三级；中、重级别纳入防治闭环' },
      { k: '闭环回看', v: '防治闭环默认回看 60 天，可按项目调整（上限 365 天）' },
      { k: '防重复提交', v: '短时间内相同失败原因合并记录，避免重复点击污染统计数据' }
    ],
    faq: [
      { q: '识别准确率大概多少？', a: '准确率取决于作物、虫相与所选识别服务，必须用你们自己的历史数据实测才有意义。这套系统提供 AI 初判与人工复核的对照统计能力，可以在运行一段时间后看到模型在你本地作物上的真实表现，而不是拿厂商的宣传数字。' },
      { q: 'AI 认错了怎么办？', a: '这正是要配人工复核的原因。AI 只做初判，结论由农技人员确认；而且复核不会覆盖 AI 原值，两份数据都保留，既保证准确性，也留下了模型优化的语料。' },
      { q: '为什么要强调「识别不出来就报失败」？', a: '因为农业里用错药的代价不可逆——农药残留超标、错过防治窗口都是实质性损失。一个硬猜的答案比一个老实的「不知道」危险得多。所以这套实现任何一步不成立，识别结论都是空的。' },
      { q: '以后想换识别服务商麻烦吗？', a: '不麻烦。识别服务通过配置管理，提供方以适配器方式接入，切换服务主要是改配置和补一个适配实现，不需要改动业务系统。' }
    ],
    related: ['traceability', 'water-fertilizer']
  },

  /* ========================== 农业 · 溯源 ========================== */
  {
    slug: 'traceability',
    line: 'agriculture',
    name: '农产品溯源与存证',
    en: 'Traceability',
    eyebrow: 'Traceability',
    tagline: '从地块到批次，把证据链补齐',
    summary:
      '批次溯源、区块链存证、冷链监控、消费者扫码查询与监管数据上报。' +
      '溯源的价值不只是合规，更是把「投入产出算得清」变成可能。',
    overview:
      '溯源最容易做成一个空壳：页面能扫码、能显示一个批次号，但背后没有真实的记录链。' +
      '真正可用的溯源，要保证农事记录、投入品使用、质检结果与流转信息在产生时就进入系统，' +
      '而不是事后补录——后补的记录无法作为证据，也无法支撑投入产出分析。',
    pains: [
      { title: '溯源链一到田间就断', desc: '加工与流通环节有记录，但种植端的农事与投入品使用靠纸笔，链条不完整。' },
      { title: '记录口径不统一', desc: '同一个动作在不同地方写法不同，比如用药与防治混在一起，统计和追溯都会失真。' },
      { title: '监管上报靠人工整理', desc: '要报送时才临时从各处凑数据，既费时又容易出错。' }
    ],
    capabilities: [
      { icon: 'box', title: '批次溯源', desc: '以批次为主线串联建档、农事、加工、质检与流转记录。', tags: ['批次主线', '全环节串联', '正反追溯'] },
      { icon: 'link', title: '区块链存证', desc: '关键节点数据做存证，防止事后被篡改，提升证据可信度。', tags: ['关键节点存证', '防篡改', '可验证'] },
      { icon: 'file', title: '冷链与物流', desc: '冷链温控与物流环节信息纳入追溯链路，覆盖采后流转。', tags: ['冷链监控', '物流记录', '环节完整'] },
      { icon: 'spark', title: '消费者扫码查询', desc: '面向消费者开放扫码查询入口，把溯源转化为品牌信任。', tags: ['扫码查询', '面向消费者', '信任背书'] },
      { icon: 'clipboard', title: '监管数据上报', desc: '按监管要求的格式归集与上报数据，减少临时凑数。', tags: ['监管报送', '数据归集', '减少人工'] },
      { icon: 'bell', title: '资质与效期提醒', desc: '资质证照到期提醒，避免因证照过期影响销售与认证。', tags: ['资质台账', '到期提醒', '合规管理'] }
    ],
    how: [
      { title: '统一记录口径', desc: '先把农事记录类型与取值统一，尤其是投入品使用与防治动作的区分。' },
      { title: '建立批次主档', desc: '按地块与生产周期建立批次，作为所有记录的主线。' },
      { title: '过程数据即时录入', desc: '农事、投入品、质检与采收在发生时即录入，不做事后补录。' },
      { title: '关键节点存证', desc: '选择对可信度影响最大的节点做存证，形成可验证的证据链。' },
      { title: '开放查询与上报', desc: '面向消费者开放扫码查询，同时按监管要求归集上报数据。' }
    ],
    specs: [
      { k: '记录口径', v: '农事记录类型统一字典，明确区分「用药记录」（投入品使用）与「病虫害防治记录」（防治动作）' },
      { k: '记录归属', v: '关联记录必须属于本农场且类型符合要求，否则拒绝；重复关联幂等' },
      { k: '存证方式', v: '关键节点数据做区块链存证，凭据经编码处理' },
      { k: '覆盖环节', v: '批次、加工、质检、销售、物流、冷链与消费者反馈均有对应记录' },
      { k: '监管对接', v: '监管渠道与报送报表独立管理，可按要求扩展' }
    ],
    faq: [
      { q: '区块链存证在农业里到底解决什么问题？', a: '解决「记录可被事后修改」这一信任问题。关键节点数据存证后，一旦有人试图改动，验证时就能发现。它的价值不在于技术本身，而在于让追溯结果可以被第三方采信。' },
      { q: '我们已经做了绿色认证，还需要系统吗？', a: '需要。认证要求的是持续、可核查的记录，靠纸质台账很难在抽查时快速调出完整链条。系统化的价值在于记录在产生时就进入系统，随时可查、可导出。' },
      { q: '为什么强调「用药记录」和「病虫害防治记录」要分开？', a: '两者含义不同：前者记录投入品的使用（用了什么、用了多少），后者记录防治这个动作本身（针对什么、怎么处置）。混在一起会导致投入品统计不准、追溯时也说不清到底用了什么药。' },
      { q: '消费者扫码能看到什么？', a: '可以按批次展示生产环节的关键信息与存证情况。具体开放哪些字段建议按品牌策略确定，既提供可信度，也不泄露不该公开的生产细节。' }
    ],
    related: ['farm-inventory', 'pest-ai']
  },

  /* ====================== 农业 · 四业态一套平台 ====================== */
  {
    slug: 'four-business-types',
    line: 'agriculture',
    name: '大田·设施·畜牧·水产四业态',
    en: 'Multi-Business',
    eyebrow: 'Multi-Business',
    tagline: '一套平台管四种业态，多农场之间数据不串',
    summary:
      '多数同行只做种植。种植、养殖与水产的差异很大，但如果分属不同系统，' +
      '集团的投入产出就永远算不到一起。四业态共用一个租户、各自独立闭环，是这套平台的取舍。',
    overview:
      '农业不是一种生产：大田看地块与农时，设施看棚内环境调控，畜牧看畜群与栏舍，' +
      '水产看塘口与水质。它们的对象模型差别很大，但管理的诉求是一致的——' +
      '记录要完整、成本算得清、异常能追责。把它们放到同一套平台里，' +
      '换来的是集团层面的口径统一；代价是数据模型必须做得足够抽象。',
    pains: [
      { title: '种植和养殖各上一套系统', desc: '不同业态分属不同软件，集团层面对比与汇总只能靠人工拼表。' },
      { title: '多农场之间数据容易串', desc: '权限只做到账号级，农场之间数据隔离不严，越权风险高。' },
      { title: '换个业态就要重新选型', desc: '平台只适配单一业态，业务扩张时又要重新走一遍采购与实施。' }
    ],
    capabilities: [
      { icon: 'leaf', title: '大田种植', desc: '地块、品种、种植计划、农事记录与灌溉管理按地块组织。', tags: ['地块管理', '种植计划', '农事记录'] },
      { icon: 'sun', title: '设施农业', desc: '大棚环境调控、设施作业与温室档案，覆盖设施生产全过程。', tags: ['大棚档案', '环境调控', '设施作业'] },
      { icon: 'users', title: '畜牧养殖', desc: '畜群与栏舍管理、繁育、称重、免疫与投喂记录。', tags: ['畜群管理', '繁育记录', '免疫计划'] },
      { icon: 'water', title: '水产养殖', desc: '塘口管理、水质监测、投喂计划与病害、捕捞分级记录。', tags: ['塘口管理', '水质监测', '投喂计划'] },
      { icon: 'shield', title: '权限与数据隔离', desc: '角色与按钮级权限之上，再叠加「用户到农场」的数据级隔离。', tags: ['按钮级权限', '数据级隔离', '多农场'] },
      { icon: 'layers', title: '统一数据模型', desc: '环境、设备、库存、财务与报表在跨业态范围内口径一致。', tags: ['口径统一', '跨业态汇总', '集团视图'] }
    ],
    how: [
      { title: '确认业态与农场范围', desc: '先明确要纳管几种业态、几个农场，以及各自的负责人。' },
      { title: '建立农场档案', desc: '按农场建档并切换业态，确定各农场包含的经营单元。' },
      { title: '配置权限与数据范围', desc: '设定角色权限，并把用户的数据范围限定到具体农场。' },
      { title: '分业态跑通闭环', desc: '每个业态先从最痛的一个环节跑通，不追求一次性全量上线。' },
      { title: '汇总与对比', desc: '在统一口径下做跨业态的投入产出与效率对比。' }
    ],
    specs: [
      { k: '业态覆盖', v: '大田种植、设施农业、畜牧养殖、水产养殖四类，同一租户下多农场独立闭环' },
      { k: '权限模型', v: '角色与按钮级权限，叠加「用户→农场」的数据级隔离' },
      { k: '越权处理', v: '非授权请求直接拒绝，不返回数据' },
      { k: '对象模型', v: '按业态建立地块、大棚、栏舍、塘口等经营单元模型' },
      { k: '口径统一', v: '库存、财务、报表等公共域跨业态共用一套口径' }
    ],
    faq: [
      { q: '只做种植，用得上这套吗？', a: '用得上，但你可以只用种植相关的部分。四业态是能力边界，不是强制范围——按当前业态选配模块即可，将来扩展到养殖或水产时不必换系统。' },
      { q: '多农场的数据会不会互相看到？', a: '不会。权限体系在角色与按钮级权限之上，还有一层「用户到农场」的数据级隔离，非授权请求会被直接拒绝且不返回数据。' },
      { q: '不同业态的报表能合起来看吗？', a: '可以。库存、财务与报表中心是跨业态共用的公共域，口径统一，所以能做集团层面的对比与汇总，而不必人工拼表。' },
      { q: '一套平台会不会太复杂？', a: '复杂度来自业态覆盖，但使用上是分业态的：大田用户看到的是地块与农事，水产用户看到的是塘口与水质。建议按业态分批启用，避免一次性铺开。' }
    ],
    related: ['farm-machine-asset', 'farm-compliance']
  },

  /* =========================== MES · 工单 =========================== */
  {
    slug: 'mes-work-order',
    line: 'mes',
    name: 'MES 工单与工序执行',
    en: 'Work Order',
    eyebrow: 'Work Order',
    tagline: 'ERP 只知道有这张订单，车间要知道它走到哪了',
    summary:
      '工单管理、工艺路线与工序、派工与报工、批次与在制品、领退补料与缺料待办。' +
      '这一层解决的是「订单下到车间之后，到底发生了什么」。',
    overview:
      'ERP 把生产订单「扔」下来之后，车间里的事就进入了另一套逻辑：订单要拆成工单，' +
      '工单要按工艺路线拆成工序，工序要派到人、报到系统，还要领料、补料、处理缺料。' +
      '这些环节如果靠纸单和微信群流转，进度、在制品与责任都说不清。MES 补的正是这一段。',
    pains: [
      { title: '工单靠纸单流转', desc: '插单、改单、撤单随时发生，纸单跟不上变化，现场靠喊。' },
      { title: '工序走到哪不知道', desc: '问到某批货做到第几道工序了，只能打电话或走到车间去问。' },
      { title: '物料与工单脱节', desc: '领了多少、补了多少、还缺什么，与工单进度对不上，缺料常在开工时才发现。' }
    ],
    capabilities: [
      { icon: 'clipboard', title: '工单管理', desc: '生产订单拆解为车间工单与工序任务，覆盖开工、报工、完工全生命周期。', tags: ['订单拆解', '全生命周期', '异常调整'] },
      { icon: 'gear', title: '工艺路线与工序', desc: '工艺模板与工序步骤固化标准路线，工位端按序推送作业内容。', tags: ['工艺模板', '工序步骤', '按序执行'] },
      { icon: 'users', title: '派工与报工', desc: '按工序派工到人，工位端报工形成真实进度，替代事后补录。', tags: ['工序派工', '工位报工', '进度实时'] },
      { icon: 'box', title: '批次与在制品', desc: '按批次跟踪在制品所处工序与数量，减少积压与丢失。', tags: ['批次管理', '在制品跟踪', '流转清楚'] },
      { icon: 'database', title: '领料、退料与补料', desc: '线边物料的领用与补充与工单绑定，避免错料与超领。', tags: ['按单领料', '退料处理', '生产补料'] },
      { icon: 'bell', title: '缺料待办', desc: '物料缺口汇总为待办，把「开工才发现缺料」提前暴露。', tags: ['缺料汇总', '提前预警', '待办跟踪'] }
    ],
    how: [
      { title: '整理工艺与主数据', desc: '先把工艺路线、工序步骤、工作中心与物料主数据理清，这是工单能跑起来的前提。' },
      { title: '订单拆解为工单', desc: '承接上层生产订单，按工艺路线拆出工单与工序任务。' },
      { title: '排产与派工', desc: '结合产能与人员安排排产，把工序派到具体工位与人员。' },
      { title: '开工报工与流转', desc: '工位端开工、报工、完工，系统实时更新工单进度与在制品状态。' },
      { title: '物料协同与异常处理', desc: '按单领料补料，缺料与异常转入待办流程处理。' }
    ],
    specs: [
      { k: '工单主线', v: '工单为车间执行主线，向下关联工序任务、报工记录与物料领用' },
      { k: '工艺固化', v: '工艺模板与工序步骤独立维护，工单按模板生成任务' },
      { k: '报工方式', v: '工位端报工，进度来自现场操作而非事后补录' },
      { k: '批次跟踪', v: '在制品按批次跟踪所处工序、加工状态与数量' },
      { k: '物料协同', v: '领料、退料与生产补料与工单关联，并汇总缺料待办' },
      { k: '异常调整', v: '支持插单、改单、撤单等车间常见变更' }
    ],
    faq: [
      { q: '已经有 ERP 了，还要 MES 吗？', a: '看你的痛点在哪。如果问题是「车间现场看不透」——工单进度、工序执行、在制品、缺料——那 ERP 一般解决不了，这正是 MES 的职责。如果问题只在财务与订单层面，则未必需要。' },
      { q: '一线工人愿意用吗？', a: '这是决定成败的关键，比软件本身更重要。落地时要把工位端的操作做到极简——扫码开工、点选报工，避免为此增加额外文书工作。建议先在一个工段试点。' },
      { q: '基础数据很乱，能直接上吗？', a: '不建议。物料主数据、工艺路线、工作中心这些底座不规范，系统跑起来也是错的。通常需要先做一轮主数据梳理，这部分工作量往往不小于配置系统。' },
      { q: '实施一般多久？', a: '按同类项目经验，标准实施周期通常在 3 到 6 个月，分阶段推进。这只是经验区间，具体以需求调研后的实施方案与排期为准。' }
    ],
    related: ['iot-maintenance', 'mes-quality-trace']
  },

  /* =========================== MES · 质量追溯 =========================== */
  {
    slug: 'mes-quality-trace',
    line: 'mes',
    name: 'MES 质量管理与批次追溯',
    en: 'Quality & Trace',
    eyebrow: 'Quality',
    tagline: '出了问题，要能追到哪一批、哪道工序、谁做的',
    summary:
      '质检计划与质检单、质量缺陷与返修、批次链与条码流转。' +
      '质量管理不是加一道检验，而是让每一条质量记录都能挂到具体的批次与工序上。',
    overview:
      '质量问题的处理成本，很大程度取决于「定位速度」：出了不良，能不能立刻知道涉及哪些批次、' +
      '哪些工序、哪台设备、哪个人？如果这些信息散在纸质检验单和聊天记录里，' +
      '追溯就变成一次人工考古。质量管理与追溯要做的，是让记录在产生时就挂在正确的位置上。',
    pains: [
      { title: '出了不良，定位靠翻记录', desc: '涉及批次、工序、设备要一个个去问、去翻单子，处理窗口被拖长。' },
      { title: '检验结果与批次对不上', desc: '检验做了，但记录与具体批次、工序没有关联，无法支撑召回判断。' },
      { title: '返修与不良没有闭环', desc: '返修申请、处置结果与不良原因统计脱节，同样的问题反复发生。' }
    ],
    capabilities: [
      { icon: 'clipboard', title: '质检计划与质检单', desc: '按检验项设定计划，检验结果以单据形式留档并关联批次。', tags: ['检验计划', '检验单据', '关联批次'] },
      { icon: 'shield', title: '质量缺陷管理', desc: '记录不良类型与处置方式，为质量分析与改进提供数据。', tags: ['不良类型', '处置记录', '统计分析'] },
      { icon: 'gear', title: '返修与处置申请', desc: '返修申请与处置流程线上化，结果回到批次记录形成闭环。', tags: ['返修申请', '处置流程', '结果回填'] },
      { icon: 'link', title: '批次链追溯', desc: '以批次为链，正反向追溯原料、工序与成品之间的对应关系。', tags: ['正向追溯', '反向追溯', '批次链'] },
      { icon: 'code', title: '条码流转', desc: '通过条码完成收货、上架、拣货、退货与盘点，流转过程可查。', tags: ['条码收货', '条码拣货', '盘点核对'] },
      { icon: 'chart', title: '质量数据统计', desc: '按工序、批次与时段统计不良分布，定位高频问题环节。', tags: ['不良分布', '按工序统计', '问题定位'] }
    ],
    how: [
      { title: '确定检验项与标准', desc: '按产品与工序明确要看什么、判定标准是什么。' },
      { title: '建立质检计划', desc: '把检验项落到具体工序与批次上，形成可执行的计划。' },
      { title: '现场检验与记录', desc: '检验结果即时录入并关联批次，不留事后补录的空当。' },
      { title: '不良处置与返修', desc: '不良进入处置或返修流程，结果回填到批次记录。' },
      { title: '追溯演练与复盘', desc: '定期做一次追溯演练，检验从成品能否一路追到原料。' }
    ],
    specs: [
      { k: '单据主线', v: '质检以计划与质检单为主线，检验结果与批次、工序关联' },
      { k: '不良与返修', v: '质量缺陷与返修申请独立管理，处置结果回填形成闭环' },
      { k: '批次链', v: '批次链与批次周转独立管理，支撑正反向追溯' },
      { k: '条码体系', v: '收货、上架、拣货、退货与盘点均可通过条码执行' },
      { k: '追溯范围', v: '覆盖原料批次、工序加工与成品之间的对应关系' }
    ],
    faq: [
      { q: '追溯要做到什么粒度才算够？', a: '取决于你的风险场景与客户要求。通用的判断方法：假设明天发生一次质量事故，你需要多快定位到涉及范围？如果要求当天给出受影响批次清单，那记录粒度就必须支持按批次与工序反查，而不是按天汇总。' },
      { q: '已经有检验流程了，为什么还要系统？', a: '纸质流程的问题不在检验本身，而在于记录与批次、工序没有自动关联。系统化的价值是让记录在产生时就挂在正确的位置上，追溯时能直接反查，不需要人工考古。' },
      { q: '条码是必须的吗？', a: '不是必须，但强烈建议。条码是把「人记录」变成「系统记录」的最低成本手段，能显著降低录入错误与漏记。可以按工段分批上。' },
      { q: '返修数据有什么用？', a: '返修与不良的统计是发现高频问题的入口。按工序与批次统计不良分布，往往能定位到具体环节——是工艺参数、设备状态还是操作差异，这比凭印象改进要可靠得多。' }
    ],
    related: ['mes-work-order', 'iot-line-monitoring']
  }
]

// 追加批次二（工业物联网 + 农业运营与合规）
solutions.push(...iotSolutions, ...farmOpsSolutions)

export const getSolution = (slug) => solutions.find((s) => s.slug === slug) || null
